import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Blackbox testing node.js apps",
  "description": "",
  "published": "2012-07-23T00:00:00.000Z",
  "redirect_from": ["/blog/blackbox-testing-node-js-apps/swizec/4884"],
  "categories": "Application programming interface, Black-box testing, Hypertext Transfer Protocol, JavaScript, Node.js, Servers, Uncategorized, Unit testing, Web application",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`One of the best features in `}<a parentName="p" {...{
        "href": "http://www.djangoproject.com",
        "title": "Django (Web framework)"
      }}>{`Django`}</a>{`is the test client that comes with the test framework -> blackbox testing baby, yeah!`}</p>
    <p><img parentName="p" {...{
        "src": "https://farm3.static.flickr.com/2215/1972546648_e63db0846e_m.jpg",
        "alt": "Black box flight recorder",
        "title": "Black box flight recorder"
      }}></img></p>
    <p><a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Black-box_testing",
        "title": "Black-box testing"
      }}>{`Blackbox testing`}</a>{` is my favourite kind of testing. Much more useful than `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Unit_testing",
        "title": "Unit testing"
      }}>{`unit tests`}</a>{` and generally a lot easier to write, maintain and deal with. And that's because rather than making sure all your functions and deep internals are behaving like they should, you only test what really matters in a web app.`}</p>
    <p><em parentName="p">{`Does the right output happen for this specific input?`}</em></p>
    <p>{`Your users don't care that you have full unit test coverage. They will never see the output of all your internal functions and methods and database drivers and whatnot. All they care is that if they put in `}<em parentName="p">{`X`}</em>{` they get the correct `}<em parentName="p">{`Y`}</em>{` out.`}</p>
    <p>{`The two main benefits of blackbox testing for a developer are:`}</p>
    <ol>
      <li parentName="ol">{`Low maintenance tests - you don't have to rewrite the whole `}<a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Test_suite",
          "title": "Test suite"
        }}>{`test suite`}</a>{` when refactoring the deep internals of your app`}</li>
      <li parentName="ol">{`Integration tests - since you're only matching input to output you are testing your whole chain, everything from routing, to database connections and the business logic of the app itself`}</li>
    </ol>
    <p>{`Although it's still prudent to write enough of these tests to check for correctly returning errors, making sure your app doesn't crash on strange inputs and so on.`}</p>
    <p>{`It's also important not to forget unit tests for core business logic. That's algorithmic stuff, algorithms should have unit tests attached to them. But in general, web apps aren't algorithms, they're systems. And systems need blackbox tests.`}</p>
    <h2 {...{
      "id": "doing-it-in-nodejs"
    }}>{`Doing it in node.js`}</h2>
    <p>{`Ok so blackbox testing is an awesome fit for web apps. But how do you do them?`}</p>
    <p>{`In django this is simple, when in a test you just say something like`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`response = self.client.get("/my/url")
self.assertEqual(response.status, 200)
`}</code></pre>
    <p>{`and check the response is what you expected. The test client automagically ensures there is a fake server running, that it takes requests, responds and is then torn down when you're done testing.`}</p>
    <p>{`Until recently the only way I knew of doing this with node.js has been to run the server and send it requests. But that's a bit lame, after all, I want to just run the tests. Of course I'm going to forget to run the server every single time.`}</p>
    <p>{`Not to mention you have to keep restarting it when you change the code.`}</p>
    <p>{`Enter `}<a parentName="p" {...{
        "href": "https://github.com/visionmedia/supertest"
      }}>{`Supertest`}</a>{`, stage left.`}</p>
    <p>{`Supertest is a high-level http test library based on superagent, which in turn is a high-level abstraction thingy for doing http requests.`}</p>
    <p>{`It takes any type of server object and lets you run requests and check for expected results in a very simple manner. If need be, you can even drop down to the low-level API's for http requests.`}</p>
    <p>{`A simple (mocha) test might look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var app = require("../app.js");

describe("GET /users", function () {
  it("respond with json", function (done) {
    request(app)
      .get("/user")
      .set("Accept", "application/json")
      .expect("Content-Type", /json/)
      .expect(200, done);
  });
});
`}</code></pre>
    <p>{`Where `}<em parentName="p">{`app`}</em>{` is, for example, an express.js server - `}<em parentName="p">{`request`}</em>{` is our test client. We are just checking that sending a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Hypertext_Transfer_Protocol",
        "title": "Hypertext Transfer Protocol"
      }}>{`GET request`}</a>{` to `}<em parentName="p">{`/user`}</em>{` will return some sort of json and that the status code will be 200.`}</p>
    <p>{`If the server isn't running when doing that `}<em parentName="p">{`require`}</em>{` then supertest will make sure it is bound to an ephemereal port and then brought back down after the tests are done.`}</p>
    <p>{`Oh and because of `}<a parentName="p" {...{
        "href": "https://github.com/visionmedia/superagent"
      }}>{`superagent`}</a>{` you can send any type of request and check for any type of response.`}</p>
    <p>{`Awesome.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/10960502/foreman-nodejs-mongoskin-how-to-connect-to-mongodb-from-mongo-cli"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/93336637_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/10960502/foreman-nodejs-mongoskin-how-to-connect-to-mongodb-from-mongo-cli"
        }}>{`Foreman + NodeJS + MongoSkin: How to connect to mongodb from mongo cli?`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/11546715/node-js-api-choking-with-concurrent-connections"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/101387271_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/11546715/node-js-api-choking-with-concurrent-connections"
        }}>{`Node.js API choking with concurrent connections`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/node-js-for-true-beginners/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/101502577_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.joezimjs.com/javascript/node-js-for-true-beginners/"
        }}>{`Node.js for True Beginners`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://architects.dzone.com/articles/when-net-dev-decides-its-time"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_06_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://architects.dzone.com/articles/when-net-dev-decides-its-time"
        }}>{`When a .NET Dev Decides It's Time for Node.js`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/2353818/how-do-i-get-started-with-node-js/9629682"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/89920188_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/2353818/how-do-i-get-started-with-node-js/9629682"
        }}>{`Answer by Nevir for How do I get started with Node.js`}</a></li>
    </ul>
    <hr></hr>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=bdac80da-9a78-4071-ac4b-f2d10e0a4b65",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      